import { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { useMeetingSignalExperiment } from '@confluence/experiment-meeting-signal/entry-points/useMeetingSignalExperiment.ts';
import { isMeetingSignalAnalysisEnabled } from '@confluence/experiment-meeting-signal/entry-points/isMeetingSignalAnalysisEnabled.ts';
import { adfToPlainText } from '@confluence/experiment-meeting-signal-core/entry-points/adfToPlainText.ts';
import type { MeetingSignalInput } from '@confluence/experiment-meeting-signal-core/entry-points/meetingSignalTypes.ts';
import { useMeetingSignalDetection } from '@confluence/experiment-meeting-signal-instrumentation/entry-points/useMeetingSignalDetection.ts';

import { useDocChangeContext } from './DocChangeContext.tsx';
import type { RetrieveEditorTitleAndContent } from './hooks/useRetrieveEditorTitleAndContent.ts';

// The body editor has no blur hook, so content edits are evaluated on a debounce
// off doc changes. 1000 ms per the spec (STARB-3763); the detector's fire-once
// guard swallows the repeated debounced emits so at most one contentEdit event
// fires per editor mount.
const CONTENT_DEBOUNCE_MS = 1000;

export interface MeetingSignalEditorDetectionParams {
	/** The ID of the page that is open in the editor. */
	contentId: string;
	/** True after the editor view and initial collab content are both ready. */
	isEditorContentReady: boolean;
	/** Selects the page type reported by editor-load and content-edit signals. */
	isLivePage: boolean;
	/** Retrieves the loaded title + ADF body from the collab provider. */
	retrieveEditorTitleAndContent: RetrieveEditorTitleAndContent;
}

/**
 * Evaluates meeting signals from debounced content changes on regular and live
 * pages. Both page types are also evaluated once their initial collab content loads.
 * Regular pages can enter the editor directly without passing through the view
 * renderer, while live pages always open in the editor.
 *
 * Must be called inside a `DocChangeContext` provider (for example, through
 * `withDocChangeContext`).
 */
export const useMeetingSignalEditorDetection = ({
	contentId,
	isEditorContentReady,
	isLivePage,
	retrieveEditorTitleAndContent,
}: MeetingSignalEditorDetectionParams): void => {
	const { subscribeDocChange, unsubscribeDocChange } = useDocChangeContext();
	const detectContentEdit = useMeetingSignalDetection();
	const detectEditorOpen = useMeetingSignalDetection();
	const { evaluate: evaluateMeetingSignalExperimentContentEdit } = useMeetingSignalExperiment();
	const { evaluate: evaluateMeetingSignalExperimentEditorOpen } = useMeetingSignalExperiment();
	const shouldAnalyzeMeetingSignal = isMeetingSignalAnalysisEnabled();

	// Trigger 1: content-edit, debounced off doc changes.
	useEffect(() => {
		if (!shouldAnalyzeMeetingSignal) {
			return;
		}

		const handleDocChange = debounce((editorView: EditorView) => {
			const doc = editorView?.state?.doc;
			if (!doc) {
				return;
			}
			// ProseMirror gives us plain text directly — no ADF encoding needed.
			const contentText = doc.textBetween(0, doc.content.size, '\n', ' ');
			const signal = {
				pageType: isLivePage ? 'livePage' : 'page',
				trigger: 'contentEdit',
				contentText,
			} satisfies MeetingSignalInput;
			detectContentEdit(signal);
			evaluateMeetingSignalExperimentContentEdit(signal);
		}, CONTENT_DEBOUNCE_MS);

		subscribeDocChange(handleDocChange);
		return () => {
			handleDocChange.cancel();
			unsubscribeDocChange(handleDocChange);
		};
	}, [
		subscribeDocChange,
		unsubscribeDocChange,
		detectContentEdit,
		evaluateMeetingSignalExperimentContentEdit,
		isLivePage,
		shouldAnalyzeMeetingSignal,
	]);

	// Trigger 2: editor open. Wait until the editor view and initial collab content are ready, then
	// evaluate each document once. Retrieval errors are ignored (best-effort instrumentation).
	const evaluatedEditorOpenContentIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (
			!shouldAnalyzeMeetingSignal ||
			!isEditorContentReady ||
			evaluatedEditorOpenContentIdRef.current === contentId
		) {
			return;
		}
		let cancelled = false;
		retrieveEditorTitleAndContent()
			.then(({ title, adf }) => {
				if (cancelled) {
					return;
				}
				evaluatedEditorOpenContentIdRef.current = contentId;
				const signal = {
					pageType: isLivePage ? 'livePage' : 'page',
					trigger: 'load',
					titleText: title,
					contentText: adfToPlainText(adf as unknown as Record<string, unknown>),
				} satisfies MeetingSignalInput;
				detectEditorOpen(signal);
				evaluateMeetingSignalExperimentEditorOpen(signal);
			})
			.catch(() => {
				// Best-effort instrumentation — ignore collab retrieval errors after readiness.
			});
		return () => {
			cancelled = true;
		};
	}, [
		contentId,
		isEditorContentReady,
		isLivePage,
		retrieveEditorTitleAndContent,
		detectEditorOpen,
		evaluateMeetingSignalExperimentEditorOpen,
		shouldAnalyzeMeetingSignal,
	]);
};
